/**
 * ValuationCard Component
 *
 * Dashboard widget for startup valuation.
 * State A: No valuation — CTA to start wizard.
 * State B: Has valuation — compact chart + suggested range + actions.
 *
 * Feature gating: free tier sees Berkus only (other methods locked).
 */

import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, ActivityIndicator, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { BarChart3, RefreshCw, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { useQuery } from '@tanstack/react-query';

import AnimatedPressable from '@/components/AnimatedPressable';
import { FootballFieldChart } from './FootballFieldChart';
import { ValuationDisclaimer } from './ValuationDisclaimer';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';
import { useThemeColors } from '@/lib/useThemeColors';
import { supabase } from '@/lib/supabase';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { reportError } from '@/lib/errors';
import { mode } from '@/lib/config/app-mode';
import type { ValuationResult } from '@/lib/valuation/types';
import type { ValuationMethodBar } from './FootballFieldChart';
import { COPY } from '@/lib/commerce/copy';

// ============================================================================
// Method display config
// ============================================================================

const METHOD_LABELS: Record<string, string> = {
  berkus: 'Berkus Method',
  scorecard: 'Scorecard',
  'risk-factor': 'Risk Factor Sum.',
  'vc-method': 'VC Method',
};

// ============================================================================
// Helpers
// ============================================================================

function formatValuation(amount: number, currency: string = 'CHF'): string {
  if (amount >= 1_000_000_000)
    return `${currency} ${(amount / 1e9).toFixed(amount % 1e9 === 0 ? 0 : 1)}B`;
  if (amount >= 1_000_000)
    return `${currency} ${(amount / 1e6).toFixed(amount % 1e6 === 0 ? 0 : 1)}M`;
  if (amount >= 1_000)
    return `${currency} ${(amount / 1e3).toFixed(amount % 1e3 === 0 ? 0 : 1)}K`;
  return `${currency} ${amount.toFixed(0)}`;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

// ============================================================================
// Component
// ============================================================================

export function ValuationCard() {
  const colors = useThemeColors();
  const router = useRouter();
  // valuation_results is keyed by startup_profiles.id (B3 fix) — resolve the
  // caller's startup via team membership, NOT auth user.id.
  const { startupId } = useMyStartup();

  const hasFullValuation = useSubscriptionStore((s) => s.hasFeature('valuation_full'));

  // Fetch latest valuation result
  const { data: latestValuation, isLoading } = useQuery({
    queryKey: ['valuation', 'latest', startupId],
    queryFn: async () => {
      // In mock mode, don't hit the DB — return null (State A) until
      // the wizard populates the cache via setQueryData (State B).
      if (mode.isMock) return null;

      // JUSTIFIED as any: 'valuation_results' table not in generated Supabase types yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('valuation_results')
        .select('*')
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        reportError(error, { context: 'ValuationCard.fetchLatest', level: 'warning' });
        throw error;
      }
      return data as ValuationResult | null;
    },
    enabled: !!startupId,
    staleTime: 30_000,
  });

  // Transform ValuationResult into chart bars
  const chartBars: ValuationMethodBar[] = useMemo(() => {
    if (!latestValuation?.methods) return [];

    return Object.entries(latestValuation.methods)
      .filter(([_, result]) => result.applicable)
      .map(([method, result]) => {
        const isBerkus = method === 'berkus';
        const locked = !isBerkus && !hasFullValuation;

        // Estimate min/max from value and confidence
        const spreadFactor = 1 - result.confidence / 100;
        const spread = result.value * Math.max(spreadFactor * 0.4, 0.1);

        return {
          method,
          label: METHOD_LABELS[method] ?? method,
          min: Math.max(0, result.value - spread),
          max: result.value + spread,
          pointEstimate: result.value,
          confidence: result.confidence,
          locked,
        };
      });
  }, [latestValuation, hasFullValuation]);

  const suggestedRange = latestValuation?.suggested_range;
  const currency = suggestedRange?.currency ?? 'CHF';

  // Loading state
  if (isLoading) {
    return (
      <View
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 24,
          padding: 32,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          shadowColor: '#000',
          shadowOpacity: 0.05,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 4 },
          elevation: 3,
        }}
      >
        <ActivityIndicator size="small" color={colors.primary[500]} />
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            marginTop: 8,
          }}
        >
          Loading valuation...
        </Text>
      </View>
    );
  }

  // ── State A: No Valuation ────────────────────────────────────────
  if (!latestValuation) {
    return (
      <View
        style={{
          backgroundColor: colors.surface.primary,
          borderRadius: 24,
          padding: 24,
          shadowColor: '#000',
          shadowOpacity: 0.05,
          shadowRadius: 10,
          shadowOffset: { width: 0, height: 4 },
          elevation: 3,
        }}
      >
        {/* Icon + Description */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              backgroundColor: colors.primary[50],
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <BarChart3 size={24} color={colors.primary[500]} />
          </View>
          <View style={{ flex: 1 }}>
            <Text
              className="text-base font-semibold"
              style={{
                color: colors.text.primary,
                marginBottom: 4,
              }}
            >
              Estimate Your Valuation
            </Text>
            <Text
              className="text-xs"
              style={{
                color: colors.text.secondary,
                lineHeight: 18,
              }}
            >
              Get a data-driven valuation estimate using up to 4 established methods.
            </Text>
          </View>
        </View>

        {/* CTA Button */}
        <AnimatedPressable
          onPress={() => {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
            router.push('/(startup)/valuation-wizard');
          }}
          style={{
            backgroundColor: colors.primary[500],
            borderRadius: 14,
            paddingVertical: 14,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <BarChart3 size={18} color="#fff" />
          <Text
            className="text-base font-semibold"
            style={{
              color: '#fff',
            }}
          >
            Calculate Valuation
          </Text>
        </AnimatedPressable>

        {/* Tier hint */}
        <Text
          className="text-xs"
          style={{
            color: colors.text.tertiary,
            textAlign: 'center',
            marginTop: 10,
          }}
        >
          {hasFullValuation ? 'All 4 methods included with your plan' : COPY.valuationLockedNote}
        </Text>
      </View>
    );
  }

  // ── State B: Has Valuation (collapsible) ─────────────────────────
  return <ValuationCardExpanded
    colors={colors}
    router={router}
    latestValuation={latestValuation}
    chartBars={chartBars}
    suggestedRange={suggestedRange}
    currency={currency}
    hasFullValuation={hasFullValuation}
  />;
}

// ============================================================================
// Collapsible State B — suggested range shown first, chart expandable
// ============================================================================

function ValuationCardExpanded({
  colors,
  router,
  latestValuation,
  chartBars,
  suggestedRange,
  currency,
  hasFullValuation,
}: {
  colors: ReturnType<typeof useThemeColors>;
  router: ReturnType<typeof useRouter>;
  latestValuation: ValuationResult;
  chartBars: ValuationMethodBar[];
  suggestedRange: ValuationResult['suggested_range'] | undefined;
  currency: string;
  hasFullValuation: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setExpanded((prev) => !prev);
  }, []);

  const ExpandIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <View
      style={{
        backgroundColor: colors.surface.primary,
        borderRadius: 24,
        padding: 20,
        shadowColor: '#000',
        shadowOpacity: 0.05,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 4 },
        elevation: 3,
      }}
    >
      {/* Header: Suggested Range + expand toggle */}
      <Pressable
        onPress={toggleExpanded}
        accessibilityRole="button"
        accessibilityLabel={expanded ? 'Collapse valuation details' : 'Expand valuation details'}
        style={{ marginBottom: expanded ? 16 : 12 }}
      >
        {suggestedRange && suggestedRange.max > 0 ? (
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 14,
                    backgroundColor: colors.primary[50],
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <BarChart3 size={20} color={colors.primary[500]} />
                </View>
                <View>
                  <Text
                    className="text-xs font-semibold"
                    style={{
                      color: colors.text.tertiary,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Suggested Range
                  </Text>
                  <Text
                    className="text-lg font-bold"
                    style={{
                      color: colors.text.primary,
                      marginTop: 1,
                    }}
                  >
                    {formatValuation(suggestedRange.min, currency)} – {formatValuation(suggestedRange.max, currency)}
                  </Text>
                </View>
              </View>
              <ExpandIcon size={20} color={colors.text.tertiary} />
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, paddingLeft: 50 }}>
              <Text
                className="text-xs font-medium"
                style={{
                  color: colors.text.tertiary,
                }}
              >
                Median: {formatValuation(suggestedRange.median, currency)}
              </Text>
              {latestValuation.created_at && (
                <Text
                  className="text-xs"
                  style={{
                    color: colors.text.tertiary,
                  }}
                >
                  {timeAgo(latestValuation.created_at)}
                </Text>
              )}
            </View>
          </View>
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <BarChart3 size={20} color={colors.primary[500]} />
              <Text
                className="text-base font-semibold"
                style={{
                  color: colors.text.primary,
                }}
              >
                Valuation Result
              </Text>
            </View>
            <ExpandIcon size={20} color={colors.text.tertiary} />
          </View>
        )}
      </Pressable>

      {/* Expanded: Chart + Disclaimer */}
      {expanded && chartBars.length > 0 && (
        <Animated.View entering={FadeIn.duration(300)}>
          <View style={{ marginBottom: 16 }}>
            <FootballFieldChart
              methods={chartBars}
              suggestedRange={suggestedRange ? {
                min: suggestedRange.min,
                max: suggestedRange.max,
                median: suggestedRange.median,
              } : undefined}
              animated={true}
              currency={currency}
            />
          </View>

          {/* Compact Disclaimer */}
          <ValuationDisclaimer variant="compact" />

          {/* Upgrade prompt for free tier */}
          {!hasFullValuation && (
            <View style={{ marginTop: 10, marginBottom: 4 }}>
              <UpgradePrompt
                targetTier="starter"
                variant="banner"
                headline="Unlock all 4 valuation methods"
                lockedTitle="All 4 valuation methods"
              />
            </View>
          )}
        </Animated.View>
      )}

      {/* Action Buttons — always visible */}
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <AnimatedPressable
          onPress={() => {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            router.push('/(startup)/valuation-wizard');
          }}
          style={{
            flex: 1,
            backgroundColor: colors.surface.secondary,
            borderRadius: 12,
            paddingVertical: 11,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <RefreshCw size={14} color={colors.text.secondary} />
          <Text
            className="text-xs font-semibold"
            style={{
              color: colors.text.secondary,
            }}
          >
            Recalculate
          </Text>
        </AnimatedPressable>

        <AnimatedPressable
          onPress={() => {
            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
            router.push('/(startup)/valuation-wizard');
          }}
          style={{
            flex: 1,
            backgroundColor: colors.primary[500],
            borderRadius: 12,
            paddingVertical: 11,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Text
            className="text-xs font-semibold"
            style={{
              color: '#fff',
            }}
          >
            View Details
          </Text>
          <ChevronRight size={14} color="#fff" />
        </AnimatedPressable>
      </View>
    </View>
  );
}
